import React, { useEffect, useState } from "react";
import ApplyForMembership from "../../components/dashboard/ApplyForMembership";
import { faGift, faMapMarkerAlt, faPaw, faTaxi, faHandshake } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { routes } from "../../config/utility";
import { Link } from "react-router-dom";
import InviteButton from "../../components/InviteButton";
import { motion } from "framer-motion";
import DashboardPromoCode from "../../components/DashboardPromoCode";
import api from "../../api/api";

const PetOwnerUnpaidDashboard = () => {
  const [partners, setPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);

  useEffect(() => {
    api
      .get("/partners")
      .then((res) => {
        setPartners(res.data || []);
        setLoadingPartners(false);
      })
      .catch(() => setLoadingPartners(false));
  }, []);

  return (
    <div className="grid grid-cols-1 gap-4 sm:gap-8 px-4 sm:px-6 min-h-screen">
      {/* Hero Section */}
      <div className="lg:col-span-2 w-full p-2 sm:p-12 rounded-3xl hover:shadow-primary transition-transform transform space-y-4 sm:space-y-8">
        <h2 className="text-3xl sm:text-5xl font-extrabold text-primary text-center">
          🐾 Pamper Your Pets with PawDash! 🐾
        </h2>
        <p className="text-base sm:text-lg text-body text-center max-w-3xl mx-auto px-4">
          Join our premium membership and give your furry friends the VIP treatment they deserve!
          From luxurious walks to comfortable rides, we're here to make your pet's tail wag with joy.
        </p>
        <ApplyForMembership />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Invite Friends Section */}
        <div className="bg-gradient-to-r from-primary to-accent text-white p-4 sm:p-6 rounded-lg shadow-lg flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-center sm:text-left">
            <h3 className="text-lg font-semibold mb-1">Invite Your Friends 🎉</h3>
            <p className="text-sm sm:text-base">Book walks for your pets together!</p>
          </div>
          <InviteButton />
        </div>

        {/* Promotion Section */}
        <DashboardPromoCode />
      </div>

      {/* Recommended Partners Section */}
      <section className="py-8 sm:py-16 lg:col-span-2">

        <h2 className="text-2xl font-bold text-tblue mb-2">🐶 Recommended Partners</h2>
        {loadingPartners ? (
          <div className="text-center py-8 text-gray-400">Loading partners...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-4">
            {partners.length === 0 ? (
              <div className="col-span-full text-center text-gray-400">No partners found.</div>
            ) : (
              partners.map((partner) => (
                <div
                  key={partner.id}
                  className="min-w-[280px] md:min-w-[320px] lg:min-w-[360px] bg-white shadow-lg rounded-xl p-6 flex items-center space-x-4 border border-gray-200 hover:shadow-xl transition-transform hover:scale-105 snap-center"
                >
                  {/* Partner Icon */}
                  <div className=" text-white rounded-full shadow-lg flex items-center justify-center w-12 h-12">

                    {partner.image_url ? (
                      <img
                        src={partner.image_url}
                        alt={partner.name}
                        className="w-12 h-12 object-cover rounded-full"
                      />
                    ) : (
                      <FontAwesomeIcon icon={faHandshake} className="text-2xl" />
                    )}
                  </div>
                  {/* Partner Info */}
                  <div>
                    <h3 className="font-semibold text-lg">{partner.name}</h3>
                    <p className="text-sm text-gray-600 line-clamp-2">{partner.description}</p>
                    <a
                      href={routes.delivery}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 font-semibold mt-2 block"
                    >
                      Book Now →
                    </a>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* How Our Booking System Works */}
      <section className="py-8 lg:col-span-2">
        <h2 className="text-3xl sm:text-5xl font-extrabold text-center text-purple-700 mb-8 sm:mb-16">
          🐾 How PawDash Works
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-12 px-4 sm:px-0">
          {[
            {
              title: "1️⃣ Schedule a Walk",
              description:
                "Choose the best time for your pet's walk. Select a trusted walker near you and customize the route.",
              icon: faPaw,
              color: "text-purple-600",
              url: routes.walk,
            },
            {
              title: "2️⃣ Book a Pet Taxi",
              description:
                "Need a ride for your pet? Book a pet-friendly taxi to the vet, groomer, or daycare hassle-free.",
              icon: faTaxi,
              color: "text-yellow-500",
              url: routes.delivery,
            },
            {
              title: "3️⃣ Real-time Tracking",
              description:
                "Get live updates on your pet's location, with photos and notifications during their walk or ride.",
              icon: faMapMarkerAlt,
              color: "text-green-500",
              url: routes.walk,
            },
          ].map((step, index) => (
            <Link to={step.url} key={index} className="h-full">
              <div
                className="bg-white p-6 sm:p-12 rounded-3xl shadow-lg hover:shadow-xl transition-transform hover:scale-105 text-center flex flex-col items-center h-full min-h-[340px] w-full"
              >
                {/* Step Icon */}
                <FontAwesomeIcon
                  icon={step.icon}
                  className={`text-4xl sm:text-6xl ${step.color} mb-4 sm:mb-6`}
                />

                {/* Step Title */}
                <h3 className="text-xl sm:text-2xl font-bold text-purple-700 mb-2 sm:mb-4">
                  {step.title}
                </h3>

                {/* Step Description */}
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed flex-1">
                  {step.description}
                </p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PetOwnerUnpaidDashboard;